import React, { useState, useRef } from 'react';
import { MatchTeamAssignment, FootballPosition, GroupMemberProfile } from '../types';
import { Sparkles, Move, RefreshCw, ArrowLeftRight, UserCheck, Shield, ChevronDown, Activity, Info, X } from 'lucide-react';

interface TacticalPitchProps {
  teamA?: MatchTeamAssignment;
  teamB?: MatchTeamAssignment;
  membersMap: Map<string, GroupMemberProfile>;
  isEditable?: boolean;
  onUpdateTeam?: (teamKey: 'teamA' | 'teamB', updatedTeam: MatchTeamAssignment) => void;
}

// Preset pitch relative coordinates (x%, y%) for default positions
const POSITION_COORDINATES: Record<FootballPosition, { x: number; y: number }> = {
  GK: { x: 50, y: 88 },
  CB: { x: 50, y: 72 },
  LB: { x: 22, y: 70 },
  RB: { x: 78, y: 70 },
  LWB: { x: 20, y: 60 },
  RWB: { x: 80, y: 60 },
  CDM: { x: 50, y: 58 },
  CM: { x: 50, y: 48 },
  CAM: { x: 50, y: 35 },
  LM: { x: 22, y: 45 },
  RM: { x: 78, y: 45 },
  LW: { x: 22, y: 25 },
  RW: { x: 78, y: 25 },
  ST: { x: 50, y: 18 },
  CF: { x: 50, y: 22 },
};

const ALL_POSITIONS: FootballPosition[] = ['GK', 'CB', 'LB', 'RB', 'CDM', 'CM', 'CAM', 'LW', 'RW', 'ST'];

export const TacticalPitch: React.FC<TacticalPitchProps> = ({
  teamA,
  teamB,
  membersMap,
  isEditable = false,
  onUpdateTeam
}) => {
  const [activeTab, setActiveTab] = useState<'both' | 'teamA' | 'teamB'>('teamA');
  const [selectedPlayerKey, setSelectedPlayerKey] = useState<{ teamKey: 'teamA' | 'teamB'; idx: number } | null>(null);
  const [swapTargetIdx, setSwapTargetIdx] = useState<number | null>(null);
  const pitchRef = useRef<HTMLDivElement>(null);

  const getTeamAvgOvr = (team?: MatchTeamAssignment) => {
    if (!team || team.players.length === 0) return 0;
    const total = team.players.reduce((sum, p) => {
      const mem = membersMap.get(p.memberProfileId);
      return sum + (mem?.footballCard.overall || 70);
    }, 0);
    return (total / team.players.length).toFixed(1);
  };

  const handlePositionMove = (
    teamKey: 'teamA' | 'teamB',
    playerIdx: number,
    newX: number,
    newY: number
  ) => {
    if (!isEditable || !onUpdateTeam) return;
    const targetTeam = teamKey === 'teamA' ? teamA : teamB;
    if (!targetTeam) return;

    const updatedPlayers = [...targetTeam.players];
    updatedPlayers[playerIdx] = {
      ...updatedPlayers[playerIdx],
      pitchX: Math.max(8, Math.min(92, newX)),
      pitchY: Math.max(8, Math.min(92, newY)),
    };

    onUpdateTeam(teamKey, { ...targetTeam, players: updatedPlayers });
  };

  const handlePositionChange = (
    teamKey: 'teamA' | 'teamB',
    playerIdx: number,
    newPos: FootballPosition
  ) => {
    if (!isEditable || !onUpdateTeam) return;
    const targetTeam = teamKey === 'teamA' ? teamA : teamB;
    if (!targetTeam) return;

    const coords = POSITION_COORDINATES[newPos] || { x: 50, y: 50 };
    const updatedPlayers = [...targetTeam.players];
    updatedPlayers[playerIdx] = {
      ...updatedPlayers[playerIdx],
      position: newPos,
      pitchX: coords.x,
      pitchY: coords.y,
    };

    onUpdateTeam(teamKey, { ...targetTeam, players: updatedPlayers });
  };

  const handleSwapPlayers = (
    teamKey: 'teamA' | 'teamB',
    idx1: number,
    idx2: number
  ) => {
    if (!isEditable || !onUpdateTeam || idx1 === idx2) return;
    const targetTeam = teamKey === 'teamA' ? teamA : teamB;
    if (!targetTeam) return;

    const updatedPlayers = [...targetTeam.players];
    const tempX = updatedPlayers[idx1].pitchX;
    const tempY = updatedPlayers[idx1].pitchY;
    const tempPos = updatedPlayers[idx1].position;

    updatedPlayers[idx1] = {
      ...updatedPlayers[idx1],
      pitchX: updatedPlayers[idx2].pitchX,
      pitchY: updatedPlayers[idx2].pitchY,
      position: updatedPlayers[idx2].position,
    };

    updatedPlayers[idx2] = {
      ...updatedPlayers[idx2],
      pitchX: tempX,
      pitchY: tempY,
      position: tempPos,
    };

    onUpdateTeam(teamKey, { ...targetTeam, players: updatedPlayers });
    setSwapTargetIdx(null);
  };

  // Touch drag tracking for mobile
  const handleTouchMove = (
    e: React.TouchEvent,
    teamKey: 'teamA' | 'teamB',
    playerIdx: number
  ) => {
    if (!isEditable || !pitchRef.current) return;
    const touch = e.touches[0];
    const pitchRect = pitchRef.current.getBoundingClientRect();
    const newX = ((touch.clientX - pitchRect.left) / pitchRect.width) * 100;
    const newY = ((touch.clientY - pitchRect.top) / pitchRect.height) * 100;
    handlePositionMove(teamKey, playerIdx, newX, newY);
  };

  // Click on Pitch to move selected player
  const handlePitchClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!selectedPlayerKey || !isEditable || !pitchRef.current) return;
    const rect = pitchRef.current.getBoundingClientRect();
    const clickX = ((e.clientX - rect.left) / rect.width) * 100;
    const clickY = ((e.clientY - rect.top) / rect.height) * 100;

    handlePositionMove(
      selectedPlayerKey.teamKey,
      selectedPlayerKey.idx,
      clickX,
      clickY
    );
  };

  const getRoleColor = (pos: FootballPosition) => {
    if (pos === 'GK') return 'bg-amber-400 text-slate-950 border-amber-300';
    if (['CB', 'LB', 'RB'].includes(pos)) return 'bg-blue-500 text-white border-blue-400';
    if (['CDM', 'CM', 'CAM', 'LM', 'RM'].includes(pos)) return 'bg-emerald-500 text-slate-950 border-emerald-300';
    return 'bg-rose-500 text-white border-rose-400';
  };

  const currentSelectedTeam = selectedPlayerKey?.teamKey === 'teamA' ? teamA : teamB;
  const currentSelectedAssigned = selectedPlayerKey && currentSelectedTeam ? currentSelectedTeam.players[selectedPlayerKey.idx] : null;
  const currentSelectedMember = currentSelectedAssigned ? membersMap.get(currentSelectedAssigned.memberProfileId) : null;

  const renderTeamOnPitch = (
    team: MatchTeamAssignment,
    teamKey: 'teamA' | 'teamB',
    isTopHalf: boolean = false
  ) => {
    return (
      <>
        {team.players.map((p, idx) => {
          const mem = membersMap.get(p.memberProfileId);
          const displayName = mem?.displayName || 'Player';
          const avatar = mem?.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100';
          const ovr = mem?.footballCard.overall || 75;
          const isSelected = selectedPlayerKey?.teamKey === teamKey && selectedPlayerKey?.idx === idx;

          // Adjust Y for split screen if both teams shown
          const displayY = isTopHalf ? p.pitchY * 0.45 : 55 + (p.pitchY * 0.4);

          return (
            <div
              key={`${teamKey}_${p.memberProfileId}_${idx}`}
              className={`absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center cursor-pointer transition-transform z-20 touch-none ${
                isSelected ? 'scale-125 z-30' : 'hover:scale-110'
              }`}
              style={{ left: `${p.pitchX}%`, top: `${displayY}%` }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPlayerKey({ teamKey, idx });
              }}
              draggable={isEditable}
              onDragEnd={(e) => {
                if (!pitchRef.current) return;
                const rect = pitchRef.current.getBoundingClientRect();
                const newX = ((e.clientX - rect.left) / rect.width) * 100;
                const newY = ((e.clientY - rect.top) / rect.height) * 100;
                handlePositionMove(teamKey, idx, newX, newY);
              }}
              onTouchMove={(e) => handleTouchMove(e, teamKey, idx)}
            >
              {/* Badge & Avatar */}
              <div
                className={`relative rounded-full p-0.5 sm:p-1 border-2 shadow-2xl transition-all ${
                  isSelected ? 'ring-4 ring-amber-400 shadow-amber-500/50 scale-110' : ''
                }`}
                style={{ borderColor: team.color, backgroundColor: 'rgba(9,13,22,0.9)' }}
              >
                <img src={avatar} alt={displayName} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover" />
                <span className="absolute -top-1 -right-1 bg-amber-400 text-slate-950 text-[8px] sm:text-[9px] font-black px-1 sm:px-1.5 py-0.2 rounded-full border border-black shadow">
                  {ovr}
                </span>
              </div>

              {/* Position Tag & Name */}
              <div className="bg-slate-950/95 text-white px-1.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-black mt-0.5 sm:mt-1 border border-slate-700/80 shadow-lg flex items-center gap-1 whitespace-nowrap">
                <span className={`px-1 rounded text-[8px] sm:text-[9px] font-extrabold ${getRoleColor(p.position)}`}>
                  {p.position}
                </span>
                <span className="text-slate-100 font-bold max-w-[65px] sm:max-w-none truncate">{displayName}</span>
              </div>
            </div>
          );
        })}
      </>
    );
  };

  return (
    <div className="space-y-4">
      {/* Team Tabs & Formation Controls */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 bg-slate-900/90 p-2.5 sm:p-3 rounded-2xl border border-slate-800">
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-0.5 sm:pb-0">
          <button
            onClick={() => {
              setActiveTab('teamA');
              setSelectedPlayerKey(null);
            }}
            className={`px-3 py-1.5 rounded-xl text-xs font-black transition whitespace-nowrap flex items-center gap-1.5 ${
              activeTab === 'teamA' ? 'bg-emerald-500 text-slate-950 shadow-md shadow-emerald-500/20' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: teamA?.color || '#10B981' }} />
            {teamA?.name || 'Team Green'} ({getTeamAvgOvr(teamA)})
          </button>

          <button
            onClick={() => {
              setActiveTab('teamB');
              setSelectedPlayerKey(null);
            }}
            className={`px-3 py-1.5 rounded-xl text-xs font-black transition whitespace-nowrap flex items-center gap-1.5 ${
              activeTab === 'teamB' ? 'bg-blue-500 text-white shadow-md shadow-blue-500/20' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: teamB?.color || '#3B82F6' }} />
            {teamB?.name || 'Team Blue'} ({getTeamAvgOvr(teamB)})
          </button>

          <button
            onClick={() => {
              setActiveTab('both');
              setSelectedPlayerKey(null);
            }}
            className={`px-3 py-1.5 rounded-xl text-xs font-black transition whitespace-nowrap ${
              activeTab === 'both' ? 'bg-amber-500 text-slate-950 shadow-md shadow-amber-500/20' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Both
          </button>
        </div>

        {/* Formation Selector */}
        {isEditable && (
          <div className="flex items-center justify-between sm:justify-end gap-2 border-t sm:border-t-0 border-slate-800 pt-2 sm:pt-0">
            <span className="text-[11px] text-slate-400 font-bold uppercase">Formation:</span>
            <select
              value={activeTab === 'teamB' ? teamB?.formation : teamA?.formation}
              onChange={(e) => {
                const key = activeTab === 'teamB' ? 'teamB' : 'teamA';
                const teamObj = key === 'teamA' ? teamA : teamB;
                if (teamObj && onUpdateTeam) {
                  onUpdateTeam(key, { ...teamObj, formation: e.target.value });
                }
              }}
              className="bg-slate-950 border border-slate-700 text-emerald-400 font-mono text-xs font-black rounded-xl px-2.5 py-1 focus:outline-none"
            >
              <option value="2-3-1">2-3-1 (7v7)</option>
              <option value="1-2-1">1-2-1 (5v5)</option>
              <option value="2-2-2">2-2-2 (7v7)</option>
              <option value="3-2-1">3-2-1 (7v7)</option>
            </select>
          </div>
        )}
      </div>

      {/* Interactive Helper Banner */}
      <div className="bg-slate-950/80 border border-slate-800 p-2.5 rounded-xl flex items-center justify-between text-[11px] text-slate-300">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-emerald-400 shrink-0" />
          <span>
            <strong>Interactive Pitch:</strong> Tap any player to inspect stats or change position. {isEditable && 'Drag tokens or tap field to move!'}
          </span>
        </div>
        {selectedPlayerKey && (
          <button
            onClick={() => setSelectedPlayerKey(null)}
            className="text-slate-400 hover:text-white text-[10px] uppercase font-black px-2 py-0.5 rounded bg-slate-800"
          >
            Deselect
          </button>
        )}
      </div>

      {/* Visual Football Pitch (Sleek Tactical Dark Board) */}
      <div
        ref={pitchRef}
        onClick={handlePitchClick}
        className="relative w-full aspect-[3/4] max-w-md mx-auto rounded-3xl overflow-hidden bg-[#0d131f] border border-white/10 shadow-2xl p-4 select-none cursor-crosshair"
      >
        {/* Pitch Lines (Modern White Tactical Board Style) */}
        <div className="absolute inset-3 border border-white/20 rounded-2xl pointer-events-none">
          {/* Halfway Line */}
          <div className="absolute top-1/2 left-0 right-0 h-px bg-white/20 -translate-y-1/2" />
          {/* Center Circle */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-28 h-28 rounded-full border border-white/20 flex items-center justify-center">
            <div className="w-2 h-2 rounded-full bg-white/30" />
          </div>
          {/* Top Penalty Box */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-24 border-b border-x border-white/20 rounded-b-xl" />
          {/* Bottom Penalty Box */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-48 h-24 border-t border-x border-white/20 rounded-t-xl" />
        </div>

        {/* Render Players on Pitch */}
        {activeTab === 'both' ? (
          <>
            {teamA && renderTeamOnPitch(teamA, 'teamA', true)}
            {teamB && renderTeamOnPitch(teamB, 'teamB', false)}
          </>
        ) : activeTab === 'teamA' && teamA ? (
          renderTeamOnPitch(teamA, 'teamA', false)
        ) : activeTab === 'teamB' && teamB ? (
          renderTeamOnPitch(teamB, 'teamB', false)
        ) : null}
      </div>

      {/* Interactive Selected Player Tactical Card & Drawer */}
      {selectedPlayerKey && currentSelectedAssigned && currentSelectedMember && (
        <div className="bg-slate-900 border-2 border-emerald-500/50 rounded-3xl p-4 sm:p-5 shadow-2xl space-y-4 animate-fadeIn">
          <div className="flex items-start justify-between gap-3 border-b border-slate-800 pb-3">
            <div className="flex items-center gap-3">
              <div className="relative">
                <img
                  src={currentSelectedMember.avatar}
                  alt={currentSelectedMember.displayName}
                  className="w-14 h-14 rounded-2xl object-cover border-2 border-emerald-400 shadow-md"
                />
                <span className="absolute -bottom-1 -right-1 bg-amber-400 text-slate-950 font-black text-xs px-1.5 rounded-full border border-black">
                  {currentSelectedMember.footballCard.overall}
                </span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="font-extrabold text-lg text-white">{currentSelectedMember.displayName}</h4>
                  <span className="text-xs font-mono font-bold text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded-md border border-amber-400/30">
                    #{currentSelectedMember.jerseyNumber}
                  </span>
                </div>
                <p className="text-xs text-slate-300 font-medium flex items-center gap-2 mt-0.5">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-black ${getRoleColor(currentSelectedAssigned.position)}`}>
                    Position: {currentSelectedAssigned.position}
                  </span>
                  <span>• {currentSelectedMember.preferredFoot} Foot</span>
                  <span>• {currentSelectedMember.stats.matchesPlayed} Matches</span>
                </p>
              </div>
            </div>

            <button
              onClick={() => setSelectedPlayerKey(null)}
              className="p-1.5 rounded-xl bg-slate-800 text-slate-400 hover:text-white transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* FIFA Attributes Grid */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center bg-slate-950 p-3 rounded-2xl border border-slate-800">
            <div>
              <span className="text-[10px] text-slate-400 uppercase font-extrabold block">PAC</span>
              <span className="text-sm font-black text-emerald-400">{currentSelectedMember.footballCard.pace}</span>
            </div>
            <div>
              <span className="text-[10px] text-slate-400 uppercase font-extrabold block">SHO</span>
              <span className="text-sm font-black text-amber-400">{currentSelectedMember.footballCard.shooting}</span>
            </div>
            <div>
              <span className="text-[10px] text-slate-400 uppercase font-extrabold block">PAS</span>
              <span className="text-sm font-black text-teal-300">{currentSelectedMember.footballCard.passing}</span>
            </div>
            <div>
              <span className="text-[10px] text-slate-400 uppercase font-extrabold block">DRI</span>
              <span className="text-sm font-black text-cyan-300">{currentSelectedMember.footballCard.dribbling}</span>
            </div>
            <div>
              <span className="text-[10px] text-slate-400 uppercase font-extrabold block">DEF</span>
              <span className="text-sm font-black text-blue-400">{currentSelectedMember.footballCard.defending}</span>
            </div>
            <div>
              <span className="text-[10px] text-slate-400 uppercase font-extrabold block">PHY</span>
              <span className="text-sm font-black text-purple-400">{currentSelectedMember.footballCard.physical}</span>
            </div>
          </div>

          {/* Interactive Controls for Organizer/Admin or Editable Mode */}
          {isEditable && (
            <div className="space-y-3 pt-2">
              <div className="space-y-1.5">
                <span className="text-xs font-black text-slate-300 block uppercase tracking-wider">
                  Quick Position Switch:
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {ALL_POSITIONS.map((pos) => {
                    const isCurrent = currentSelectedAssigned.position === pos;
                    return (
                      <button
                        key={pos}
                        onClick={() => handlePositionChange(selectedPlayerKey.teamKey, selectedPlayerKey.idx, pos)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-mono font-black transition ${
                          isCurrent
                            ? 'bg-emerald-500 text-slate-950 border-2 border-emerald-300 shadow-md scale-105'
                            : 'bg-slate-950 hover:bg-slate-800 text-slate-300 border border-slate-800'
                        }`}
                      >
                        {pos}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Swap Player Dropdown */}
              {currentSelectedTeam && currentSelectedTeam.players.length > 1 && (
                <div className="pt-2 border-t border-slate-800 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-bold text-slate-300 flex items-center gap-1.5">
                    <ArrowLeftRight className="w-4 h-4 text-amber-400" /> Swap Tactical Position With:
                  </span>
                  <select
                    onChange={(e) => {
                      const targetIndex = parseInt(e.target.value);
                      if (!isNaN(targetIndex)) {
                        handleSwapPlayers(selectedPlayerKey.teamKey, selectedPlayerKey.idx, targetIndex);
                      }
                    }}
                    defaultValue=""
                    className="bg-slate-950 border border-slate-700 text-amber-400 font-bold text-xs rounded-xl px-3 py-2 focus:outline-none"
                  >
                    <option value="" disabled>Select teammate to swap...</option>
                    {currentSelectedTeam.players.map((otherP, otherIdx) => {
                      if (otherIdx === selectedPlayerKey.idx) return null;
                      const otherMem = membersMap.get(otherP.memberProfileId);
                      return (
                        <option key={otherIdx} value={otherIdx}>
                          {otherP.position} – {otherMem?.displayName || 'Teammate'}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
